import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { ROLE_LABEL, isReadOnlyOn, viewForPath, type ViewId } from '@aithora/shared';
import { useConsoleRole } from '../session/SessionContext';

/** The page on screen, or null outside the router (a component rendered on its own, as in a
 *  unit test). Being inside a router never changes for a mounted component, so the hook order
 *  is stable. */
function useCurrentView(): ViewId | null {
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const path = inRouter ? useLocation().pathname : null;
  return path ? viewForPath(path) : null;
}

/** The short form, for a page's access line and a control's hover. */
export const READ_ONLY_SHORT = 'Read only: your account can look, not change.';
export const READ_ONLY_REASON = READ_ONLY_SHORT + ' The server refuses changes too.';
/** Why a control is off while a Super Admin views the console as another role. */
export const viewAsReason = (label: string) => `Read only: you are viewing as ${label}. Nothing you do here is sent, and your own role is unchanged.`;

/**
 * True for roles that may read but never write (Partner), and whenever a Super Admin is
 * viewing as another role. From the session only.
 */
export function useReadOnly(): boolean {
  const { realRole, viewingAs } = useConsoleRole();
  const view = useCurrentView();
  return viewingAs !== null || isReadOnlyOn(realRole, view);
}

/** The sentence a disabled control carries, or null when writes are allowed. */
export function useReadOnlyReason(): string | null {
  const { realRole, viewingAs } = useConsoleRole();
  const view = useCurrentView();
  if (viewingAs) return viewAsReason(ROLE_LABEL[viewingAs]);
  return isReadOnlyOn(realRole, view) ? READ_ONLY_REASON : null;
}

/**
 * A button that turns itself off, with the reason on hover and for screen readers, when
 * the signed-in role is read-only, when viewing as another role, or when
 * `disabledReason` is given. The server enforces the same rule, so this is a courtesy,
 * not the lock.
 */
export function ActionButton({
  children,
  disabledReason,
  className = 'btn btn--primary btn--sm',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode; disabledReason?: string | null }) {
  const roReason = useReadOnlyReason();
  const reason = roReason ?? disabledReason ?? null;
  return (
    <button
      type="button"
      {...rest}
      className={className}
      disabled={!!reason || rest.disabled}
      aria-disabled={!!reason || rest.disabled}
      title={reason ?? rest.title}
      data-readonly={roReason ? 'true' : undefined}
    >
      {children}
      {reason && <span className="sr-only"> ({reason})</span>}
    </button>
  );
}

/** A one-line note shown above a block whose actions are hidden for a read-only role. */
export function ReadOnlyNote() {
  const { realRole, viewingAs } = useConsoleRole();
  const view = useCurrentView();
  // Viewing as has its own banner across the top; this note is for the Partner, and the
  // Developer on the pages they only read.
  if (viewingAs || !isReadOnlyOn(realRole, view)) return null;
  return (
    <p className="readonly-note" role="note">
      Read only. You can see everything here; changes are made by Aithora or the facility.
    </p>
  );
}
